import { z } from 'zod'

import { gqlQuery, gqlMutate } from '@hapana/shared/web/services/requests.service'

import {
  capitalSitePaymentGatewayQuery,
  createStripeCapitalSessionMutation,
  CAPITAL_SITE_PAYMENT_GATEWAY_QUERY_KEY,
} from './graphql/capital.queries'

/**
 * The site's active gateway, as far as Capital cares: only Stripe sites can mount the Connect
 * embedded components, and mounting them needs the platform publishable key.
 *
 * Parsed rather than cast. A cast over an untyped GraphQL response would silently yield
 * `undefined` if the shape ever drifts, which here reads as "not eligible" — Capital would quietly
 * disappear from the nav with nothing to explain why.
 */
export const siteGatewayForCapitalSchema = z.object({
  countryCode: z.string().nullable(),
  paymentGatewayType: z.string().nullable(),
  paymentGatewayPublishableKey: z.string().nullable(),
})

export type SiteGatewayForCapital = z.infer<typeof siteGatewayForCapitalSchema>

/** Whole-response shapes, so nothing is cast on the way out of the GraphQL client. */
const siteGatewayResponseSchema = z.object({
  sitePaymentGateway: siteGatewayForCapitalSchema.nullish(),
})

const capitalSessionResponseSchema = z.object({
  createStripeCapitalSession: z.object({ clientSecret: z.string().min(1) }).nullish(),
})

const NO_GATEWAY: SiteGatewayForCapital = {
  countryCode: null,
  paymentGatewayType: null,
  paymentGatewayPublishableKey: null,
}

export const getSitePaymentGatewayForCapital = gqlQuery({
  key: CAPITAL_SITE_PAYMENT_GATEWAY_QUERY_KEY,
  query: capitalSitePaymentGatewayQuery,
})

export const fetchSiteGatewayForCapital = async (
  siteId: string
): Promise<SiteGatewayForCapital> => {
  // `sitePaymentGateway` throws for a site with no active gateway — deliberately, because on a
  // checkout path that is a real misconfiguration. Capital only ever asks "is this site eligible",
  // so the answer here is simply "no" rather than an error the nav gate would have to render.
  const data = await getSitePaymentGatewayForCapital
    .query({ queryKey: [CAPITAL_SITE_PAYMENT_GATEWAY_QUERY_KEY, { siteId }] })
    .catch(() => null)

  // A caught failure is the expected no-gateway case, not drift — returning before the parse keeps
  // it out of the console, so a logged parse failure always means something really did change.
  if (!data) {
    return NO_GATEWAY
  }

  // safeParse, not parse: a drifted shape must not blank the whole nav with a thrown error. It
  // fails closed to "not eligible" and says so in the console, so the cause is findable.
  const parsed = siteGatewayResponseSchema.safeParse(data)
  if (!parsed.success) {
    console.error('[Capital] Unexpected sitePaymentGateway shape:', parsed.error.issues)
    return NO_GATEWAY
  }

  return parsed.data.sitePaymentGateway ?? NO_GATEWAY
}

/**
 * Mint a short-lived Account Session client secret for the Capital components. Connect calls this
 * again on its own whenever the secret expires, so it must stay a plain fetch.
 */
export const createCapitalSessionSecret = async (siteId: string): Promise<string> => {
  const data = await gqlMutate({
    mutation: createStripeCapitalSessionMutation,
    variables: { siteId },
  })

  // Parsed, so a drifted or empty response fails loudly here. Connect only surfaces a generic
  // failure when `fetchClientSecret` resolves to nothing, which would be far harder to diagnose.
  const parsed = capitalSessionResponseSchema.safeParse(data)
  if (!parsed.success || !parsed.data.createStripeCapitalSession) {
    throw new Error('Stripe returned no client secret for the Capital session')
  }

  return parsed.data.createStripeCapitalSession.clientSecret
}
